import { Component, DestroyRef, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { API, AttachmentAssignmentDTO, AttachmentDTO, AttachmentFolderDTO, Pagination } from '../../../services/api/api';
import { QueryParamService } from '../../../services/lists/query-params';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import {
  AttachmentEditEmitter,
  DragItemStyle,
  Entity,
  SbBooleanToggleModel,
  TableWrapperOptions,
} from '../../../services/global/global-interfaces';
import { AttachmentService } from '../../../services/attachment.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { UploadService } from '../../../services/upload/upload.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-attachment-explorer',
  templateUrl: './attachment-explorer.component.html',
  styleUrls: ['./attachment-explorer.component.scss'],
  standalone: false,
})
export class AttachmentExplorerComponent implements OnInit, OnDestroy {
  @Input() entity: Entity;
  @Input() isEmbedded: boolean;
  @ViewChild('folderInput') folderInput: ElementRef;
  attachments: AttachmentDTO[];
  attachmentFolders: AttachmentFolderDTO[];
  selectedFolderId: number;
  folderDragoverId: number | null;
  folderName: string;
  folderCreationMode: boolean;
  owned: boolean;
  loading: boolean;
  initialBooleans: SbBooleanToggleModel[];
  queryParamsParsed: boolean;
  show: boolean;
  selectedAttachment: AttachmentDTO;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  selectedMode: string;
  folderPagination: BehaviorSubject<Pagination>;
  folderString: string;
  folderSearch: string;
  hover: boolean;
  list = [];
  options: TableWrapperOptions;
  dragging: any;
  dragItemStyle: DragItemStyle;
  checkForThumbnailTimer: any;
  private readonly destroy$ = new Subject();
  protected folderView: boolean = true;

  constructor(
    private toaster: ToasterService,
    private api: API,
    private queryParamService: QueryParamService,
    private errorHandler: ErrorService,
    public util: UtilService,
    private modal: ModalService,
    private attachmentService: AttachmentService,
    private translate: TranslateService,
    private element: ElementRef,
    private uploadService: UploadService,
    private destroyRef: DestroyRef
  ) {
    this.attachments = [];
    this.selectedFolderId = null;
    this.folderPagination = new BehaviorSubject<Pagination>({
      page: 1,
      count: 99,
      pages: -1,
      resultcount: -1,
    });
  }

  ngOnDestroy(): void {
    this.destorySub();
  }

  ngOnInit(): void {
    this.initBoolToggles();
    if (!this.queryParamsParsed) {
      this.parseQueryParams().then(() => {
        this.refreshBoolToggles();
        this.refreshQueryParams();
        this.queryParamsParsed = true;
        this.getFolders();
      });
    }
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.attachmentFolders) {
        this.folderString = this.util.getPaginationString(
          this.folderPagination.getValue().page,
          this.folderPagination.getValue().count,
          this.attachmentFolders.length,
          this.folderPagination.getValue().resultcount,
          'folder'
        );
      }
    });
    this.attachmentService.createSubjects().then(() => {
      this.attachmentService.attachmentChange.pipe(takeUntil(this.destroy$)).subscribe(data => {
        if (data) {
          this.pushToAttachments(data);
        }
      });
      this.attachmentService.linkUploadMode.pipe(takeUntil(this.destroy$)).subscribe(data => {
        if (data) {
          this.openLinkSettings(data);
        }
      });
    });
    this.uploadService._finishedUpload.pipe(takeUntil(this.destroy$)).subscribe((file: any) => {
      if (file && file._type === 'AttachmentDTO' && this.attachments && this.attachments?.length >= 0) {
        this.attachments.pop();
        this.attachments.unshift(file as AttachmentDTO);
      }
    });
  }

  dragStart($event: any) {
    const event: DragEvent = $event.event;
    this.dragging = $event.item;
    if (event.dataTransfer.setDragImage) {
      const dragDummy = (this.element.nativeElement as Element).querySelector('.drag-dummy');
      event.dataTransfer.setDragImage(dragDummy, 0, 0);
    }
    this.dragItemStyle = {
      left: event.screenX,
      top: event.screenY,
    };
  }

  dragEnd() {
    this.dragging = undefined;
  }

  dragOver(event: DragEvent, folderId: number) {
    if (this.dragging) {
      event.preventDefault();
      this.folderDragoverId = folderId;
    }
  }

  moveDragElement(event: DragEvent) {
    if (this.dragging) {
      this.dragItemStyle.left = event.pageX;
      this.dragItemStyle.top = event.pageY - 70;
    }
  }

  dragOut(event: DragEvent) {
    event.preventDefault();
    this.folderDragoverId = null;
  }

  drop(event: DragEvent, folderId: number) {
    if (this.dragging) {
      this.moveToFolder(folderId, this.dragging);
    }
  }

  createNewFolder() {
    if (this.folderName) {
      const folder: AttachmentFolderDTO = new AttachmentFolderDTO();
      folder.name = this.folderName;
      this.api
        .shared_AttachmentFolder_createRootFolder(folder)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.toggleFolderCreation();
            this.folderName = null;
          })
        )
        .subscribe({
          next: data => {
            this.pushToFolders(data);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  doFolderSearch(search: string) {
    this.folderSearch = search;
    this.getFolders();
  }

  destorySub() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  getFolders() {
    this.api
      .shared_AttachmentFolder_retrieveAll(
        {
          search: this.util.encodeSearchQueryParam(false, this.folderSearch),
        },
        this.folderPagination
      )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: data => {
          if (!data) {
            data = [];
          }
          this.attachmentFolders = data;
          this.folderString = this.util.getPaginationString(
            this.folderPagination.getValue().page,
            this.folderPagination.getValue().count,
            data.length,
            this.folderPagination.getValue().resultcount,
            'folder'
          );
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  moveToFolder(folderId: number, attachment: AttachmentDTO) {
    attachment.folder = this.getFolderByID(folderId); // null refers to root folder
    this.api
      .shared_Attachment_editAttachment(attachment)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.dragging = undefined;
          this.folderDragoverId = null;
        })
      )
      .subscribe({
        next: () => {
          this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.itemMovedToFolder', { itemName: attachment.name }));
          this.requestDebounced();
          this.getFolders();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  removeFromFolder(folderId: number, attachment: AttachmentDTO) {
    this.api
      .shared_AttachmentFolder_moveToRoot(attachment.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.dragging = undefined;
          this.folderDragoverId = null;
        })
      )
      .subscribe({
        next: () => {
          this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.itemMovedToFolder', { itemName: attachment.name }));
          this.requestDebounced();
          this.getFolders();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  toggleFolderCreation($event?: MouseEvent) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.folderCreationMode = !this.folderCreationMode;
    if (this.folderCreationMode && this.folderInput) {
      setTimeout(() => {
        this.folderInput.nativeElement.focus();
      }, 100);
    }
  }

  toggleFolder(folder: AttachmentFolderDTO | null) {
    this.selectedFolderId = folder ? folder.id : null;
    this.requestDebounced();
  }

  createNewFile() {
    this.selectedAttachment = null;
    this.show = !this.show;
  }

  linkAttachment(attachment: AttachmentDTO): Promise<any> {
    return new Promise(resolve => {
      if (this.entity && attachment.id) {
        const attachmentAttach: AttachmentAssignmentDTO = new AttachmentAssignmentDTO();
        attachmentAttach.isPublic = true;
        attachmentAttach.attachment = attachment;
        this.api
          .shared_Attachment_attachToEntity(this.entity.entity, this.entity.entityId, [attachmentAttach])
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: () => {
              resolve(true);
            },
            error: error => {
              resolve(false);
              this.errorHandler.errorHandler(error);
            },
          });
      } else {
        resolve(false);
      }
    });
  }

  openLinkSettings(data: boolean) {
    this.selectedAttachment = null;
    this.selectedMode = 'LINK';
    this.show = true;
  }

  isInEditMode(attachment: AttachmentDTO): boolean {
    let i = 0;
    for (const at of this.attachments) {
      if (attachment.id === at.id) {
        this.attachments[i] = attachment;
        return true;
      }
      i++;
    }
    return false;
  }

  checkForThumbnail(attachment: AttachmentDTO): Promise<AttachmentDTO> {
    return new Promise<AttachmentDTO>(resolve => {
      this.checkForThumbnailTimer = setTimeout(() => {
        this.api
          .shared_Attachment_retrieveById(attachment.id)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: data => {
              resolve(data);
            },
          });
      }, 45000);
    });
  }

  pushToAttachments(attachmentEmitter: AttachmentEditEmitter) {
    const attachment = attachmentEmitter.attachment;
    if (attachmentEmitter.type === 'create') {
      this.show = false;
    }
    if (attachment.id === -1) {
      // check y -1
      // Handle erros
      this.requestDebounced();
      return;
    }
    if (this.selectedFolderId) {
      this.toggleFolder(null);
    }

    this.linkAttachment(attachment).then(() => {
      // this.options.pagination.value.resultcount = this.options.pagination.value.resultcount++; // todo rem
      let i = 0;
      if (attachment.id) {
        if (this.isInEditMode(attachment)) {
          return;
        } else {
          for (const at of this.attachments) {
            if (!at.id) {
              this.attachments.splice(i, 1);
              if (!attachment.thumbnail) {
                this.checkForThumbnail(attachment).then(data => {
                  for (let b = 0; b <= this.attachments.length; b++) {
                    if (this.attachments[b]?.id === data.id) {
                      this.attachments[b] = data;
                    }
                  }
                });
              }
            }
            i++;
          }
        }
      }
      // here add
      // this.uploadingAttachments.push(attachment);
      // this.attachments.unshift(attachment);
      while (this.attachments.length > this.options.pagination.value.count) {
        // immediatly pop?
        this.attachments.pop();
      }
    });
  }

  pushToFolders(folder: AttachmentFolderDTO) {
    this.attachmentFolders.unshift(folder);
    this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.successful'));
  }

  selectAttachment(att: AttachmentDTO) {
    this.selectedAttachment = att;
    this.show = true;
  }

  initBoolToggles() {
    this.owned = false;
    this.refreshBoolToggles();
  }

  refreshBoolToggles() {
    this.initialBooleans = [];
    this.initialBooleans = [
      {
        id: 'owned',
        name: _('lang.globals.ownedAttachments'),
        value: this.owned,
        class: 'owned',
        classPreview: 'blue',
      },
    ];
  }

  parseQueryParams(): Promise<void> {
    return new Promise(res => {
      this.parseBoolQueryParams();
      this.parseFilterQueryParams().then(() => {
        res();
      });
    });
  }

  parseBoolQueryParams() {
    const owned: string = this.queryParamService.getQueryParam('owned');

    this.owned = owned === 'true';
  }

  parseFilterQueryParams() {
    return new Promise<void>(res => {
      const folderQp = this.queryParamService.getQueryParam('folder');
      if (folderQp && folderQp.length > 0) {
        this.selectedFolderId = +folderQp;
      }
      res();
    });
  }

  requestDebounced(options?: TableWrapperOptions) {
    if (this.checkForThumbnailTimer) {
      clearTimeout(this.checkForThumbnailTimer);
    }
    if (options) {
      this.options = options;
    }
    if (this.queryParamsParsed) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  handleDeleteFile() {
    this.show = false;
    this.requestDebounced();
  }

  refreshFromServer() {
    this.loading = true;
    let obs: Observable<any>;
    const obsOptions = {
      search: this.util.encodeSearchQueryParam(this.options.exactMatch, this.options.search),
      folder_id: this.folderView && this.selectedFolderId ? this.selectedFolderId : null,
      hasFolder: this.folderView ? !!this.selectedFolderId : null,
      owned: this.owned,
    };
    if (this.entity) {
      obs = this.api.shared_Attachment_retrieveAllAttachmentsForEntity(
        this.entity.entity,
        this.entity.entityId,
        obsOptions,
        this.options.pagination
      );
    } else {
      obs = this.api.shared_Attachment_retrieveAll(obsOptions, this.options.pagination);
    }
    obs
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: data => {
          if (this.entity) {
            this.attachments = [];
            for (const att of data) {
              this.attachments.push(att.attachment);
            }
          } else {
            this.attachments = data;
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  refreshQueryParams() {
    if (this.selectedFolderId) {
      this.queryParamService.clearQueryParam('folder');
      this.queryParamService.setQueryParam('folder', this.selectedFolderId.toString());
    } else {
      this.queryParamService.clearQueryParam('folder');
    }
    if (this.owned !== undefined && this.owned !== null) {
      this.queryParamService.setQueryParam('owned', '' + this.owned);
    } else {
      this.queryParamService.clearQueryParam('owned');
    }
  }

  boolChange(t: string, $e: boolean) {
    if (t === 'owned') {
      this.owned = $e;
    }
    this.requestDebounced();
  }

  deleteFolder($event: MouseEvent, folder: AttachmentFolderDTO) {
    $event.preventDefault();
    $event.stopPropagation();
    if (!folder) {
      return;
    }
    if (folder.folderSize > 0) {
      this.toaster.toast(ToastType.DANGER, this.translate.instant('lang.attachment.deleteFolderSizeWarning'));
      return;
    }
    if (folder.folderSize === 0) {
      this.modal
        .confirm({
          title: this.translate.instant('lang.attachment.deleteFolder'),
          message: this.translate.instant('lang.attachment.deleteFolderMessage', { itemName: folder.name }),
          yes: this.translate.instant('lang.globals.yes'),
          no: this.translate.instant('lang.globals.no'),
        })
        .then((result: boolean) => {
          if (result) {
            this.api
              .shared_AttachmentFolder_deleteById(folder.id)
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe({
                next: () => {
                  this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.deleteSuccess'));
                  this.getFolders();
                },
              });
          }
        });
    }
  }

  getFolderByID(folderId: number): AttachmentFolderDTO {
    if (!folderId || !this.attachmentFolders || this.attachmentFolders?.length === 0) {
      return null;
    }
    if (this.attachmentFolders.length > 0) {
      for (const folder of this.attachmentFolders) {
        if (folder.id === folderId) {
          return folder;
        }
      }
    }
    return null;
  }
}
